/**
 * AppShell — authenticated layout wrapper.
 *
 * Structure (lg+):
 *   Header (Royal Navy 2-tier with user menu)
 *   ├── Sidebar (Navy + Gold-active, 240px, lg+)
 *   └── Main content
 *   Footer (Royal Navy 4-col)
 *
 * Below the `lg` breakpoint the sidebar is hidden — the Header's MobileNav
 * drawer (Dashboard / Account / Sign out section) takes over.
 *
 * The shell fetches `/me` once on mount so the Header can render the
 * customer's display name + Sign out action. /me may 401 if the session
 * expired — lib/api.ts already triggers a redirect to /login in that case,
 * so we silently swallow the error here.
 *
 * Sign out POSTs /api/portal/auth/logout and then, regardless of the
 * response (so a stale cookie still surfaces a usable login screen), sends
 * the browser through the Stock site's /logout, which ends the Stock session
 * as well and returns to /login (lib/stockSession.ts).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Header } from '../chrome/Header';
import { Footer } from '../chrome/Footer';
import { MembershipPending } from './MembershipPending';
import type { TopBarUser } from '../chrome/TopBarSignIn';
import type { MeData } from '@customer-portal/shared';
import { apiGet, apiPost } from '../../lib/api';
import { stockLogoutUrl } from '../../lib/stockSession';

interface NavItem {
  to: string;
  label: string;
  /** When true, the item is active for any pathname starting with `to`.
   *  When false (used for `/` Dashboard), only an exact match counts. */
  startsWith: boolean;
}

// Phase-1 sidebar: only these 6 items are exposed. The remaining routes
// (Shipments / Invoices / Documents / Import guide) stay registered in
// App.tsx but are hidden from the nav until later phases.
// Labels are Japanese for the phase-1 rollout; the nav is to be unified to
// English later. See the phase-1 nav plan.
//   注文落札状況  → /orders      (OrderStatusPage, authenticated landing —
//                                  root `/` is now the public catalog)
//   落札車両      → /vehicles    (purchased / won vehicles)
//   お気に入り車両 → /stock/favorites
//   サポート      → /support
//   アカウント設定 → /account
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/orders', label: 'Order & winning-bid status', startsWith: true },
  { to: '/vehicles', label: 'Winning-bid vehicles', startsWith: true },
  { to: '/stock/favorites', label: 'Favorite vehicles', startsWith: true },
  { to: '/support', label: 'Support', startsWith: true },
  { to: '/account', label: 'Account settings', startsWith: true },
] as const;

interface MeEnvelope {
  ok: true;
  data: MeData;
}

interface Props {
  children: ReactNode;
  /** When true, drops the default vertical padding so pages can manage
   *  full-bleed hero sections themselves. */
  flush?: boolean;
}

function isActive(item: NavItem, pathname: string): boolean {
  if (item.startsWith) {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  return pathname === item.to;
}

export function AppShell({ children, flush }: Props): JSX.Element {
  const [me, setMe] = useState<MeData | null>(null);
  // 'loading' until the first /me settles. We must know the customer-scope
  // status BEFORE mounting the page children: a scope-less (unlinked) account
  // 401s on every member endpoint, and lib/api.ts turns that 401 into a
  // window.location redirect to /login — bouncing a signed-in user. Gating on
  // /me lets us render MembershipPending instead of ever calling those
  // endpoints. On a non-401 /me error we fall back to rendering children so a
  // transient blip never hides a linked member's pages.
  const [meStatus, setMeStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const location = useLocation();

  // Pick the most specific match so `/stock/favorites` highlights Favorites,
  // not both Favorites and Stock browse.
  const activeTo = NAV_ITEMS.reduce<string | null>((best, item) => {
    if (!isActive(item, location.pathname)) return best;
    if (best === null || item.to.length > best.length) return item.to;
    return best;
  }, null);

  useEffect(() => {
    const ctrl = new AbortController();
    apiGet<MeEnvelope>('/me', ctrl.signal)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setMe(res.data);
        setMeStatus('loaded');
      })
      .catch(() => {
        if (ctrl.signal.aborted) return;
        // 401 (no session) is already handled by lib/api.ts (redirect to
        // /login). Any other failure: fall back to rendering children.
        setMeStatus('error');
      });
    return () => ctrl.abort();
  }, []);

  // Signed in but not yet linked to a member (customer) — no scope.
  const noMembership = meStatus === 'loaded' && me != null && me.scopes.length === 0;

  async function handleSignOut(): Promise<void> {
    try {
      await apiPost('/auth/logout', {});
    } catch {
      // ignore — we still want to land on /login below.
    }
    // End the Stock session too: Stock's /logout clears it and returns to /login.
    window.location.assign(stockLogoutUrl(`${window.location.origin}/login`));
  }

  const user: TopBarUser | undefined = me
    ? {
        name: me.name ?? me.customer?.name ?? null,
        email: me.email ?? null,
        onSignOut: handleSignOut,
      }
    : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header user={user} />
      {/* Full-width flex row — the sidebar is pinned to the viewport's left
          edge so wide-screen layouts don't gain a gutter to its left. The
          Header / Footer keep their max-w-[1760px] centered chrome; only the
          authenticated body uses this edge-to-edge layout. */}
      <div className="flex-1 flex w-full">
        <aside
          aria-label="Primary"
          className="hidden lg:flex flex-col w-60 shrink-0 bg-brand-900 text-brand-200 self-stretch"
        >
          <nav aria-label="Primary" className="flex-1 py-5 flex flex-col gap-0.5 sticky top-20">
            {NAV_ITEMS.map((item) => {
              const active = item.to === activeTo;
              const cls = active
                ? 'px-5 py-2.5 text-sm font-semibold bg-brand-500 text-brand-950 border-l-4 border-brand-600 transition-colors duration-150'
                : 'px-5 py-2.5 text-sm font-medium text-brand-200 hover:bg-brand-800 hover:text-white border-l-4 border-transparent transition-colors duration-150';
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cls}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>
        <main
          id="main-content"
          tabIndex={-1}
          className={`flex-1 min-w-0 px-4 sm:px-6 xl:px-12 2xl:px-16 ${
            flush && !noMembership ? '' : 'py-8 lg:py-12'
          }`}
        >
          {meStatus === 'loading' ? (
            <section aria-busy="true" className="text-sm text-foreground/70">
              Loading…
            </section>
          ) : noMembership ? (
            <MembershipPending email={me?.email ?? null} onSignOut={handleSignOut} />
          ) : (
            children
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
